import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import RewardsOverviewCard from './components/RewardsOverviewCard';
import RedemptionQueueTable from './components/RedemptionQueueTable';
import RewardConfigPanel from './components/RewardConfigPanel';
import PointAdjustmentModal from './components/PointAdjustmentModal';
import AdminActivityFeed from './components/AdminActivityFeed';

const RewardsSystemAdministration = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [rewardOptions, setRewardOptions] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [showPointAdjustment, setShowPointAdjustment] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');

  // Check admin access
  const isAdmin = (user?.email === 'demo@rovynbudget.com' || user?.email === 'rovyngroupinc@gmail.com') && userProfile?.role === 'admin';

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }

    loadAdminData();
  }, [user, isAdmin, navigate]);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const [metricsData, redemptionsData, rewardsData, logsData] = await Promise.all([
        adminService?.getPlatformMetrics(),
        adminService?.getAllRedemptions(),
        adminService?.getRewardOptions(),
        adminService?.getAdminActivityLogs(20)
      ]);

      setMetrics(metricsData);
      setRedemptions(redemptionsData);
      setRewardOptions(rewardsData);
      setActivityLogs(logsData);
    } catch (err) {
      setError(err?.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleRedemptionAction = async (redemptionId, status, notes) => {
    try {
      await adminService?.updateRedemptionStatus(redemptionId, status, user?.id, notes);
      await loadAdminData();
    } catch (err) {
      setError(err?.message || 'Failed to update redemption');
    }
  };

  const handleRewardUpdate = async (rewardId, updates) => {
    try {
      await adminService?.updateRewardOption(rewardId, updates, user?.id);
      await loadAdminData();
    } catch (err) {
      setError(err?.message || 'Failed to update reward');
    }
  };

  const handlePointAdjustment = async (userId, points, reason) => {
    try {
      await adminService?.adjustUserPoints(userId, points, reason, user?.id);
      setShowPointAdjustment(false);
      setSelectedUser(null);
      await loadAdminData();
    } catch (err) {
      setError(err?.message || 'Failed to adjust points');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'redemptions', label: 'Redemptions', icon: 'Gift' },
    { id: 'rewards', label: 'Reward Config', icon: 'Settings' },
    { id: 'activity', label: 'Activity Log', icon: 'Activity' }
  ];

  const pendingRedemptions = redemptions?.filter(r => r?.status === 'pending') || [];
  const completedRedemptions = redemptions?.filter(r => r?.status === 'completed') || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-16" />
            <p className="text-muted-foreground">Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-16 md:px-24 py-24 md:py-32 max-w-[1400px]">
        {/* Page Header */}
        <div className="mb-32">
          <div className="flex items-center space-x-12 mb-12">
            <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-lg">
              <Icon name="Shield" size={28} color="var(--color-primary)" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-secondary mb-4">
                Rewards System Administration
              </h1>
              <p className="text-base text-muted-foreground">
                Manage rewards, redemptions, and point allocations across the platform
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-16 flex items-start space-x-12">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <p className="text-error text-sm flex-1">{error}</p>
              <button onClick={() => setError('')} className="text-error hover:text-error/80">
                <Icon name="X" size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="bg-card rounded-lg shadow-elevation-2 mb-24">
          <div className="flex border-b border-border overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-8 px-20 py-16 font-medium transition-smooth whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span>{tab?.label}</span>
                {tab?.id === 'redemptions' && pendingRedemptions?.length > 0 && (
                  <span className="bg-error text-white text-xs font-bold px-6 py-2 rounded-full">
                    {pendingRedemptions?.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-24">
            <RewardsOverviewCard metrics={metrics} />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
              <div className="bg-card rounded-lg shadow-elevation-2 p-24">
                <div className="flex items-center justify-between mb-20">
                  <h3 className="text-xl font-heading font-semibold text-secondary">
                    Pending Redemptions
                  </h3>
                  <span className="bg-warning/10 text-warning text-sm font-medium px-12 py-4 rounded-full">
                    {pendingRedemptions?.length} pending
                  </span>
                </div>
                {pendingRedemptions?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-32">No pending redemptions</p>
                ) : (
                  <div className="space-y-12">
                    {pendingRedemptions?.slice(0, 5)?.map((redemption) => (
                      <div key={redemption?.id} className="border border-border rounded-lg p-16">
                        <div className="flex items-start justify-between mb-8">
                          <div>
                            <p className="font-medium text-foreground">{redemption?.userName}</p>
                            <p className="text-sm text-muted-foreground">{redemption?.userEmail}</p>
                          </div>
                          <span className="text-primary font-semibold">{redemption?.pointsSpent} pts</span>
                        </div>
                        <p className="text-sm text-foreground mb-12">{redemption?.rewardTitle}</p>
                        <div className="flex space-x-8">
                          <button
                            onClick={() => handleRedemptionAction(redemption?.id, 'completed', 'Approved by admin')}
                            className="flex-1 bg-success text-white px-12 py-6 rounded-lg text-sm font-medium hover:bg-success/90 transition-smooth"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleRedemptionAction(redemption?.id, 'failed', 'Rejected by admin')}
                            className="flex-1 bg-error text-white px-12 py-6 rounded-lg text-sm font-medium hover:bg-error/90 transition-smooth"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-card rounded-lg shadow-elevation-2 p-24">
                <h3 className="text-xl font-heading font-semibold text-secondary mb-20">
                  Recent Admin Activity
                </h3>
                <AdminActivityFeed logs={activityLogs?.slice(0, 5)} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'redemptions' && (
          <RedemptionQueueTable
            redemptions={redemptions}
            onAction={handleRedemptionAction}
          />
        )}

        {activeTab === 'rewards' && (
          <RewardConfigPanel
            rewards={rewardOptions}
            onUpdate={handleRewardUpdate}
          />
        )}

        {activeTab === 'activity' && (
          <div className="bg-card rounded-lg shadow-elevation-2 p-24">
            <h3 className="text-xl font-heading font-semibold text-secondary mb-20">
              Admin Activity Log
            </h3>
            <AdminActivityFeed logs={activityLogs} showAll />
          </div>
        )}
      </main>

      {showPointAdjustment && (
        <PointAdjustmentModal
          user={selectedUser}
          onClose={() => {
            setShowPointAdjustment(false);
            setSelectedUser(null);
          }}
          onAdjust={handlePointAdjustment}
        />
      )}
    </div>
  );
};

export default RewardsSystemAdministration;